const RECOVERY_KEY = 'nova:pwa-stale-chunk-recovery';
const RECOVERY_WINDOW_MS = 60_000;

export function isStaleChunkError(error) {
  const message = String(
    error?.message ||
      error?.reason?.message ||
      error?.payload?.message ||
      error ||
      ''
  ).toLowerCase();

  return [
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'importing a module script failed',
    'chunkloaderror',
    'loading chunk',
    'failed to fetch module script',
  ].some(fragment => message.includes(fragment));
}

function canRecoverNow() {
  try {
    const raw = window.sessionStorage.getItem(RECOVERY_KEY);
    if (!raw) return true;

    const previous = JSON.parse(raw);
    const samePath = previous?.path === window.location.pathname;
    const recent =
      Number.isFinite(previous?.at) &&
      Date.now() - previous.at < RECOVERY_WINDOW_MS;

    return !(samePath && recent);
  } catch {
    return true;
  }
}

function rememberRecovery() {
  try {
    window.sessionStorage.setItem(
      RECOVERY_KEY,
      JSON.stringify({
        at: Date.now(),
        path: window.location.pathname,
      })
    );
  } catch {
    // sessionStorage can be unavailable in hardened/private browser contexts.
  }
}

async function purgeStaticCaches() {
  if (!('caches' in window)) return;

  try {
    const names = await window.caches.keys();
    await Promise.all(
      names
        .filter(name => name.startsWith('static-nova-erp-'))
        .map(name => window.caches.delete(name))
    );
  } catch {
    // Recovery still works through a hard navigation when CacheStorage fails.
  }
}

export async function recoverFromStaleChunk(error) {
  if (!isStaleChunkError(error)) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  if (!canRecoverNow()) return false;

  rememberRecovery();

  try {
    navigator.serviceWorker?.controller?.postMessage({
      type: 'PURGE_ASSET_CACHE',
    });
  } catch {
    // Best effort only.
  }

  await purgeStaticCaches();

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set('__app_refresh', String(Date.now()));
  window.location.replace(nextUrl.toString());
  return true;
}

export function installStaleChunkRecovery() {
  try {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('__app_refresh')) {
      currentUrl.searchParams.delete('__app_refresh');
      window.history.replaceState(
        window.history.state,
        '',
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
      );
    }
  } catch {
    // URL cleanup is cosmetic; never block app startup.
  }

  window.addEventListener('vite:preloadError', event => {
    const error = event?.payload || event;
    if (!isStaleChunkError(error)) return;

    event.preventDefault();
    void recoverFromStaleChunk(error);
  });

  window.addEventListener('unhandledrejection', event => {
    if (!isStaleChunkError(event?.reason)) return;

    event.preventDefault();
    void recoverFromStaleChunk(event.reason);
  });

  window.addEventListener('error', event => {
    if (!isStaleChunkError(event?.error || event?.message)) return;

    event.preventDefault();
    void recoverFromStaleChunk(event.error || event.message);
  });
}
